import { Alert } from '@material-ui/lab';
import React, { useEffect, useMemo, useState } from 'react';
import { ApiAccessSection } from './api-access-section';
import type { ApiAccessService } from './api-access-service';
import { apiAccessService } from './api-access-service';
import type { CompanyApiAccess } from './company-api-access-type';
import './manage-company-settings.scss';

type ManageCompanySettingsProps = {
  companyId?: number | null;
  companyName?: string | null;
  apiAccessService?: ApiAccessService;
};

const getDefaultApiAccessState = (companyId?: number | null): CompanyApiAccess[] => ([{
  companyId: companyId ?? 0,
  apiKey: null,
  status: 'none',
  createdAt: null,
  expiresAt: null,
}]);

export const ManageCompanySettings: React.FC<ManageCompanySettingsProps> = ({
  companyId,
  apiAccessService: apiAccessServiceProp = apiAccessService,
}) => {
  const hasCompanyId = typeof companyId === 'number' && Number.isFinite(companyId);
  const [apiAccess, setApiAccess] = useState<CompanyApiAccess[]>(() => getDefaultApiAccessState(companyId));
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const service = useMemo(() => apiAccessServiceProp, [apiAccessServiceProp]);

  useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      if (!hasCompanyId) {
        setApiAccess(getDefaultApiAccessState(companyId));
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      try {
        const result = await service.getApiAccess(companyId as number);
        if (!isCancelled) {
          setApiAccess(result);
        }
      } catch (loadError) {
        if (!isCancelled) {
          console.error('Failed to load company API access settings.', loadError);
          setError('Unable to load API access settings right now.');
          setApiAccess(getDefaultApiAccessState(companyId));
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();

    return () => {
      isCancelled = true;
    };
  }, [companyId, hasCompanyId, service]);

  const runAction = async (action: () => Promise<CompanyApiAccess>) => {
    if (!hasCompanyId) {
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const result = await action();
      setApiAccess([result]);
    } catch (actionError) {
      console.error('Failed to update company API access settings.', actionError);
      setError('Unable to update API access settings right now. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className='manage-company-settings'>
      {/* <div className='manage-company-settings__header'>
        <h2>
          API Access
        </h2>
      </div>
      <Divider className='manage-company-settings__divider' /> */}
      <div className='manage-company-settings__content'>
        {error && (
          <Alert severity='error' className='manage-company-settings__error'>
            {error}
          </Alert>
        )}

        {!hasCompanyId ? (
          <Alert severity='info' className='manage-company-settings__error'>
            API access settings are unavailable until the company record has a valid ID.
          </Alert>
        ) : (
          <ApiAccessSection
            apiAccess={apiAccess}
            isLoading={isLoading}
            isSaving={isSaving}
            onGenerateKey={() => runAction(() => service.generateKey(companyId as number))}
            onRotateKey={() => runAction(() => service.rotateKey(companyId as number))}
            onExpireKey={() => runAction(() => service.expireKey(companyId as number))}
            onRevokeKey={() => runAction(() => service.revokeKey(companyId as number))}
          />
        )}
      </div>
    </div>
  );
};
